/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { CONTENT_ACTIVITY_VIEWS, CONTENT_ACTIVITY_FETCH_EVENTS } from '@/constants/device';
import { DashboardIcon } from '../Common/Icons';
import useUserInfo from '../Hooks/useUserInfo';
import useUserEventsByTypesAndTimeRange from '../Hooks/useUserEventsByTypesAndTimeRange';
import { aggregate } from './ContentActivityUtils';
import ListContentActivity from './ListContentActivity.component';

const DEFAULT_MAX_DAYS = 30;
const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars) || {};
const configuredHistoryDays = Number(ENV_VARS.CONTENT_ACTIVITY_NUMBER_OF_DAYS);
const CONTENT_ACTIVITY_HISTORY_DAYS =
  Number.isFinite(configuredHistoryDays) && configuredHistoryDays > 0
    ? configuredHistoryDays
    : DEFAULT_MAX_DAYS;

const LocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const ContentActivity = ({
  parentBread = [],
  onActionClick: parentActionClick,
  hubRecord,
  timezone,
  loginUserTimeformat,
} = {}) => {
  const trans = useSelector(translator);

  const useruuid = hubRecord?.useruuid;
  const userTimezone = (timezone ? timezone.split(' ')[0] : '') || LocalTimezone;
  const { data: userInfo = {} } = useUserInfo({ useruuid });

  const [activeView, setActiveView] = useState(CONTENT_ACTIVITY_VIEWS.SUMMARY);
  const [rawEvents, setRawEvents] = useState([]);

  const { mutate: fetchEvents, isPending: isLoading } = useUserEventsByTypesAndTimeRange();

  const windowBounds = useMemo(() => {
    const endMoment = moment().tz(userTimezone).endOf('day');
    const startMoment = endMoment
      .clone()
      .subtract(CONTENT_ACTIVITY_HISTORY_DAYS - 1, 'days')
      .startOf('day');
    return {
      fromMs: startMoment.valueOf(),
      toMs: endMoment.valueOf(),
      startISO: startMoment.clone().utc().format(),
      endISO: endMoment.clone().utc().format(),
    };
  }, [userTimezone]);

  useEffect(() => {
    if (!useruuid) return;
    fetchEvents({
      useruuid,
      startDateTimeUTC: windowBounds.startISO,
      endDateTimeUTC: windowBounds.endISO,
      eventTypes: CONTENT_ACTIVITY_FETCH_EVENTS,
      successCallback: (userEvents) => setRawEvents(userEvents || []),
      errorCallback: () => setRawEvents([]),
    });
  }, [useruuid, windowBounds]);

  const model = useMemo(
    () => aggregate(rawEvents, userTimezone, windowBounds),
    [rawEvents, userTimezone, windowBounds]
  );

  const header = (parentBread || []).concat(
    parentActionClick
      ? {
          label: trans('CONTENTACTIVITY'),
          onClick: parentActionClick(null, null),
        }
      : {
          label: trans('DASHBOARD'),
          icon: <DashboardIcon />,
        }
  );

  return (
    <ListContentActivity
      parentBread={header}
      model={model}
      loading={isLoading}
      activeView={activeView}
      setActiveView={setActiveView}
      timezone={userTimezone}
      loginUserTimeformat={loginUserTimeformat}
      userInfo={userInfo}
    />
  );
};

ContentActivity.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  onActionClick: PropTypes.func,
  hubRecord: PropTypes.object,
  timezone: PropTypes.string,
  loginUserTimeformat: PropTypes.object,
};

export default ContentActivity;
